import { CircleUserRound, House, LogOut, Mail, Plus, Users, UsersRound } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ActionMenu } from '@/components/ActionMenu'
import type { Me } from '@/lib/api'
import { useFamilies, useFamily } from '@/lib/families'
import { chooseLevel, useFrame } from '@/lib/frame'
import { chooseSpace, useCurrentSpace } from '@/lib/spaces'
import { signOut } from '@/lib/signOut'
import type { Place } from './places'
import { CircleQuickEdit } from './CircleQuickEdit'
import { CircleDot } from './YouFace'

// The part of a circle's address after its id that another circle, and Everyone, has too (Photos stays Photos).
const SECTION = /^\/(?:circle\/[^/]+|everyone)\/(people|photos|moments|recipes|calendar)(\/|$)/

// You, at the end of a phone's tabs, the top right of a tablet or computer, and the foot of the sidebar: where you
// switch circles, as well as who's signed in, your profile (with your preferences), invites for the owner, and
// signing out. Your circles come first, each with its colour (the ring your picture wears while you're in it); a
// circle opens on the same section you're looking at. The rooms of the circle you're in come next, when it has more
// than one. On a phone, the circle's places past the tabs (Recipes, Calendar) come first. A menu on a tablet or
// computer, a sheet from the bottom on a phone. Each circle's pencil opens its quick edit.
export function ProfileMenu({
  me,
  children,
  side,
  align = 'start',
  places = [],
}: {
  me: Me
  children: ReactElement
  side?: 'top' | 'right' | 'bottom'
  align?: 'start' | 'end'
  places?: Place[]
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const families = useFamilies().data?.families ?? []
  const { familyId } = useFrame()
  const family = useFamily(familyId ?? '', { enabled: !!familyId }).data
  const space = useCurrentSpace(family)
  const section = pathname.match(SECTION)?.[1]
  const inEveryone = pathname === '/everyone' || pathname.startsWith('/everyone/')
  // A circle with only one room has nothing to switch between.
  const rooms = family && family.spaces.length > 1 && !inEveryone ? family : null
  // The circle whose quick edit is open: its name, colour and people, without leaving the page you're on.
  const [editing, setEditing] = useState<string | null>(null)
  return (
    <>
      <ActionMenu
        title="Your profile"
        side={side}
        align={align}
        trigger={children}
        label={
          <>
            <span className="block truncate text-foreground">{me.name}</span>
            <span className="block truncate">{me.email}</span>
          </>
        }
        groups={[
          {
            heading: inEveryone ? 'Everyone' : (family?.name ?? 'More'),
            actions: places.map((place) => ({
              label: place.label,
              lead: <place.icon className="size-5" active={place.active} />,
              current: place.active,
              onSelect: () => navigate(place.to),
            })),
          },
          {
            heading: 'Your circles',
            actions: [
              ...families.map((f) => ({
                label: f.name,
                lead: <CircleDot color={f.color} />,
                current: f.id === familyId && !inEveryone,
                edit: { label: `Edit ${f.name}`, onSelect: () => setEditing(f.id) },
                onSelect: () => {
                  chooseLevel('circle')
                  // From Everyone (or something opened there), or another circle; already in it, there's nowhere to go.
                  if (f.id !== familyId || !pathname.startsWith('/circle/'))
                    navigate(section ? `/circle/${f.id}/${section}` : `/circle/${f.id}`)
                },
              })),
              // What's shared with Everyone, once you're in a circle or connected with someone.
              ...(me.everyone
                ? [
                    {
                      label: 'Everyone',
                      icon: Users,
                      current: inEveryone,
                      onSelect: () => {
                        chooseLevel('everyone')
                        // Everyone opens on the section you're looking at too.
                        if (!inEveryone) navigate(section ? `/everyone/${section}` : '/everyone')
                      },
                    },
                  ]
                : []),
              { label: 'Create a circle', icon: Plus, onSelect: () => navigate('/circle/new') },
            ],
          },
          {
            heading: 'Space',
            actions: rooms
              ? rooms.spaces.map((x) => ({
                  label: x.name,
                  icon: x.kind === 'everyone' ? Users : x.kind === 'custom' ? UsersRound : House,
                  current: x.id === space?.id,
                  onSelect: () => chooseSpace(rooms.id, x.id),
                }))
              : [],
          },
          [
            // With People switched off, the circle's own page (who's here, invites, keeping a copy) is reached from here.
            ...(familyId && !me.sections.people
              ? [{ label: 'Your circle', icon: Users, onSelect: () => navigate(`/circle/${familyId}/people`) }]
              : []),
            { label: 'My profile', icon: CircleUserRound, onSelect: () => navigate('/me') },
            ...(me.admin ? [{ label: 'Invites', icon: Mail, onSelect: () => navigate('/invites') }] : []),
            { label: 'Sign out', icon: LogOut, onSelect: () => void signOut() },
          ],
        ]}
      />
      <CircleQuickEdit circleId={editing} me={me} onClose={() => setEditing(null)} />
    </>
  )
}
